import React, { useMemo, useEffect, HTMLProps } from 'react';
import { Formik, Form } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import { BaseTextFieldProps } from '@material-ui/core';
import DynamicFieldList from './dynamic-field-list';

import { Error } from '../error';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(3),
  },
  input: {
    flex: 1,
  },
  gridItem: {
    display: 'flex',
  },
  loading: {
    marginRight: theme.spacing(1),
  },
  section: {
    marginTop: theme.spacing(3),
    display: 'flex',
    alignItems: 'stretch',
  },
  actions: {
    display: 'flex',
    gap: `${theme.spacing(2)}px`,
  },
  actionBtn: {
    flex: 1,
  },
}));

export type FieldType = {
  name?: string;
  type?:
  | 'text'
  | 'password'
  | 'email'
  | 'date'
  | 'select'
  | 'radio'
  | 'switch'
  | 'number'
  | 'file'
  | 'section'
  | 'input-range'
  | 'autocomplete'
  | 'checkbox'
  | 'time'
  | 'custom';

  label: string;
  initialValue?: string | number | Date | boolean | any[];
  required?: boolean;
  disabled?: boolean;
  grid?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  options?: any[];
  multiline?: boolean;
  rows?: number;
  rowsMax?: number;
  onChange?: Function;
  multiple?: boolean;
  value?: any;
  onFileSelect?: Function;
  component?: React.ReactNode;
  placeholder?: string;
  format?: string;
  maxDateField?: string;
  minDateField?: string;
  inputProps?: HTMLProps<HTMLInputElement>;
  helperText?: BaseTextFieldProps['helperText'];
};

type DynamicFormProps = {
  fields: { [key: string]: FieldType } | any;
  validationSchema?: any;
  children?: any;
  onSubmit: any;
  error?: string;
  onChange?: Function;
  initialValue?: any;
  disabled?: boolean;
};

const FormBody = ({ form, onChange, fields, disabled, children }: any) => {
  const classes = useStyles();
  const { setFieldValue, isSubmitting } = form;
  useEffect(() => {
    if (onChange) {
      onChange(form);
    }
  }, [form, onChange]);

  useEffect(() => {
    setFieldValue('formDisabled', disabled);
  }, [setFieldValue, disabled]);

  useEffect(() => {
    const el = document.querySelector('.Mui-error, [data-error]');
    (el?.parentElement ?? el)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [isSubmitting]);

  return (
    <Form className={classes.form} noValidate>
      <DynamicFieldList fields={fields} disabled={disabled} />
      {children}
    </Form>
  );
};

export function DynamicForm({
  fields,
  validationSchema,
  onSubmit,
  error,
  onChange,
  children,
  initialValue,
  disabled,
}: DynamicFormProps) {
  const classes = useStyles();

  const initialValues = useMemo(() => {
    if (initialValue) return initialValue;
    return Object.keys(fields).reduce(
      (acc, nextField) => ({
        ...acc,
        [nextField]: fields[nextField]?.initialValue,
      }),
      {}
    );
  }, [fields, initialValue]);

  return (
    <div>
      {error && <Error className={classes.input} errorCode={error} />}
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {(form) => (
          <>
            <FormBody form={form} onChange={onChange} fields={fields} disabled={disabled}>
              {children && <div className={classes.section}>{children(form)}</div>}
            </FormBody>
          </>
        )}
      </Formik>
    </div>
  );
}

export default DynamicForm;
